import {
  AppointmentSchema,
  AvailabilitySchema,
  BundleDocSchema,
  BusinessNumberSchema,
  BusinessSchema,
  CallRecordSchema,
  ClientSchema,
  ServiceSchema,
  StaffMemberSchema,
  type Appointment,
  type Availability,
  type BundleDoc,
  type Business,
  type BusinessNumber,
  type CallRecord,
  type Client,
  type Service,
  type StaffMember,
} from '@acs/shared';
import type {
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';
import type { z } from 'zod';

/**
 * FirestoreDataConverters built from the shared zod schemas (ADR-0003 rule 1).
 * Documents are validated on read — schema drift surfaces as a loud dev error
 * rather than `undefined` rendering. `Timestamp` -> `Date` happens inside the
 * schema's coercion; writing a `Date` back auto-converts via the Web SDK.
 */

type WithId<T> = T & { id: string };

function makeConverter<T>(
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  label: string,
): FirestoreDataConverter<WithId<T>> {
  return {
    toFirestore(model: WithId<T>): DocumentData {
      const { id: _id, ...rest } = model as WithId<T> & { id: string };
      return rest as DocumentData;
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): WithId<T> {
      // `estimate`, not the SDK default of `null`: a write carrying
      // `serverTimestamp()` fires a local snapshot before the server answers,
      // and a `null` there fails the schema — which used to drop us onto the
      // raw document, whose `createdAt` is a `Timestamp` rather than a `Date`.
      // The setup gate then misread the business as finished and flashed the
      // dashboard every time the wizard saved a field (e.g. the operator).
      const raw = snapshot.data({ ...options, serverTimestamps: 'estimate' });
      const parsed = schema.safeParse(raw);
      if (!parsed.success) {
        // Fail loud in dev, log + rethrow-as-skip in prod (caught by the caller).
        console.error(`[converter:${label}] invalid document ${snapshot.id}`, parsed.error.flatten());
        if (import.meta.env.DEV) throw parsed.error;
      }
      // The raw fallback still needs `Date`s: a record written by a newer
      // gateway (e.g. an enum value this build predates) once fell through
      // with its `Timestamp`s intact, and `Intl` formatted `Timestamp.valueOf()`
      // — every such call read "Mon 10 Jan 1972, 22:49".
      const data = (parsed.success ? parsed.data : (timestampsToDates(raw) as T));
      return { ...(data as T), id: snapshot.id };
    },
  };
}

/** Deep-replaces every Firestore `Timestamp` (anything with `toDate()`) by its `Date`. */
function timestampsToDates(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || value instanceof Date) return value;
  const obj = value as Record<string, unknown>;
  if (typeof obj.toDate === 'function') return (obj.toDate as () => Date)();
  if (Array.isArray(value)) return value.map(timestampsToDates);
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, timestampsToDates(v)]));
}

export const businessConverter = makeConverter<Business>(BusinessSchema, 'business');
export const serviceConverter = makeConverter<Service>(ServiceSchema, 'service');
export const staffConverter = makeConverter<StaffMember>(StaffMemberSchema, 'staff');
export const appointmentConverter = makeConverter<Appointment>(AppointmentSchema, 'appointment');
export const callConverter = makeConverter<CallRecord>(CallRecordSchema, 'call');
export const clientConverter = makeConverter<Client>(ClientSchema, 'client');
export const availabilityConverter = makeConverter<Availability>(AvailabilitySchema, 'availability');
export const bundleConverter = makeConverter<BundleDoc>(BundleDocSchema, 'bundle');
export const numberConverter = makeConverter<BusinessNumber>(BusinessNumberSchema, 'number');

export type BusinessDoc = WithId<Business>;
export type ServiceDoc = WithId<Service>;
export type StaffDoc = WithId<StaffMember>;
export type AppointmentDoc = WithId<Appointment>;
export type CallDoc = WithId<CallRecord>;
export type ClientDoc = WithId<Client>;
export type AvailabilityDoc = WithId<Availability>;
export type BundleDocWithId = WithId<BundleDoc>;
export type NumberDoc = WithId<BusinessNumber>;
